import React, { forwardRef } from "react";
import { cn } from "../../utils/cn";

/**
 * DESIGN.md Button system.
 *
 * Variants:
 *  - primary           near-black bg, white text, 12px radius (default)
 *  - secondary         white bg, ink text, hairline border, 12px radius
 *  - secondary-on-dark same shape as secondary; intended on dark surfaces (no inversion)
 *  - danger            coral bg, white text — used for destructive confirms
 *  - legal             link-blue bg, 2px radius, 600 weight (cookie/legal)
 *  - pill              white bg, ink text, pill radius (pricing sub-system only)
 *  - icon-circular     40x40 white circle with hairline border
 *  - ghost             transparent bg, ink text — quiet inline actions
 *  - link              link-blue text, no chrome
 *
 * Sizes:
 *  - md (default)      16x24 padding, 16px type
 *  - sm                12x16 padding, 14px type
 *  - lg                20x32 padding, 16px type
 */
const VARIANT_CLASSES = {
  primary:
    "bg-ink text-onInk border border-transparent hover:bg-primary-active active:bg-primary-active disabled:bg-borderStrong disabled:text-onInk/80",
  secondary:
    "bg-canvas text-ink border border-hairline hover:bg-surface-soft active:bg-surface-soft disabled:text-borderStrong disabled:border-hairline",
  "secondary-on-dark":
    "bg-canvas text-ink border border-transparent hover:bg-surface-soft active:bg-surface-soft",
  danger:
    "bg-signature-coral text-white border border-transparent hover:brightness-110 active:brightness-95",
  legal:
    "bg-link text-white border border-transparent hover:bg-link-active active:bg-link-active",
  pill:
    "bg-canvas text-ink border border-hairline hover:bg-surface-soft active:bg-surface-soft",
  "icon-circular":
    "bg-canvas text-ink border border-hairline hover:bg-surface-soft active:bg-surface-soft",
  ghost:
    "bg-transparent text-ink border border-transparent hover:bg-surface-soft active:bg-surface-soft",
  link:
    "bg-transparent text-link border border-transparent hover:text-link-active active:text-link-active",
};

const SIZE_CLASSES = {
  sm: "px-4 py-3 text-body-md",
  md: "px-6 py-4 text-button",
  lg: "px-8 py-5 text-button",
};

// The link variant has no chrome, so it only takes the type size, never the padding.
const LINK_SIZE_CLASSES = {
  sm: "text-body-md",
  md: "text-button",
  lg: "text-button",
};

const RADIUS_CLASSES = {
  primary: "rounded-lg",
  secondary: "rounded-lg",
  "secondary-on-dark": "rounded-lg",
  danger: "rounded-lg",
  legal: "rounded-xs",
  pill: "rounded-pill",
  "icon-circular": "rounded-full",
  ghost: "rounded-md",
  link: "rounded-none",
};

const BASE =
  "inline-flex items-center justify-center gap-2 font-medium leading-tight transition-colors duration-150 focus:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-60 select-none";

export const Button = forwardRef(function Button(
  {
    as: As = "button",
    variant = "primary",
    size = "md",
    type = "button",
    className,
    children,
    leadingIcon,
    trailingIcon,
    fullWidth,
    ...rest
  },
  ref
) {
  const isIcon = variant === "icon-circular";
  const sizeClass = isIcon
    ? "h-10 w-10 p-0 text-body-md"
    : variant === "link"
    ? LINK_SIZE_CLASSES[size] || LINK_SIZE_CLASSES.md
    : SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const variantClass = VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary;
  const radiusClass = RADIUS_CLASSES[variant] || "rounded-lg";

  const props = As === "button" ? { type, ...rest } : rest;

  return (
    <As
      ref={ref}
      className={cn(
        BASE,
        sizeClass,
        variantClass,
        radiusClass,
        fullWidth && "w-full",
        className
      )}
      {...props}
    >
      {leadingIcon ? <span className="inline-flex shrink-0">{leadingIcon}</span> : null}
      {children}
      {trailingIcon ? <span className="inline-flex shrink-0">{trailingIcon}</span> : null}
    </As>
  );
});

export default Button;
